import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { QuickViewModalShellComponent } from '@clinic-platform/ui-components';

import { AuthService } from '../../../core/auth/auth.service';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { DeviceTypeDetailItem } from '../data-access/device-types.models';
import { DeviceTypesService } from '../data-access/device-types.service';

@Component({
  selector: 'app-device-type-quick-view-modal',
  standalone: true,
  imports: [DatePipe, QuickViewModalShellComponent],
  template: `
    <lib-quick-view-modal-shell
      [isOpen]="isOpen && !!deviceTypeId"
      [title]="title"
      subtitle="Read-only device type context without leaving the current workflow."
      [isLoading]="isLoading"
      loadingText="Loading device type..."
      [errorText]="errorText"
      (close)="close.emit()"
    >
      @if (deviceType) {
        <div class="quick-view-grid">
          <div class="quick-view-field">
            <span>Device Type</span>
            <strong>{{ deviceType.name }}</strong>
            <div class="quick-view-id-meta">{{ deviceType.id }}</div>
          </div>

          <div class="quick-view-field">
            <span>Description</span>
            <strong>{{ deviceType.description || '—' }}</strong>
          </div>

          <div class="quick-view-field">
            <span>Created</span>
            <strong>{{ deviceType.createdAt | date: 'dd.MM.yyyy HH:mm' }}</strong>
          </div>

          <div class="quick-view-field">
            <span>Updated</span>
            <strong>{{ deviceType.updatedAt | date: 'dd.MM.yyyy HH:mm' }}</strong>
          </div>
        </div>
      }
    </lib-quick-view-modal-shell>
  `,
})
export class DeviceTypeQuickViewModalComponent implements OnChanges {
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly deviceTypesService = inject(DeviceTypesService);

  @Input() public isOpen = false;
  @Input() public deviceTypeId: string | null = null;
  @Input() public companyIdOverride: string | null = null;
  @Input() public title = 'Device Type Quick View';

  @Output() public readonly close = new EventEmitter<void>();

  protected isLoading = false;
  protected errorText: string | null = null;
  protected deviceType: DeviceTypeDetailItem | null = null;

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.isOpen) {
      return;
    }

    if (changes['isOpen'] || changes['deviceTypeId'] || changes['companyIdOverride']) {
      this.load();
    }
  }

  private get resolvedCompanyId(): string | undefined {
    const override = this.companyIdOverride?.trim() ?? '';
    if (override) {
      return override;
    }

    if (this.authService.getCurrentRole() === 'system_admin') {
      return this.systemAdminContext.getCompanyId()?.trim() || undefined;
    }

    return undefined;
  }

  private load(): void {
    const deviceTypeId = this.deviceTypeId?.trim() ?? '';
    if (!this.isOpen || !deviceTypeId) {
      return;
    }

    this.isLoading = true;
    this.errorText = null;
    this.deviceType = null;

    this.deviceTypesService.getById(deviceTypeId, this.resolvedCompanyId).subscribe({
      next: (item) => {
        this.deviceType = item;
        this.isLoading = false;
      },
      error: (error: unknown) => {
        this.errorText = resolveHttpErrorMessageKey(error, {
          fallback: 'Failed to load device type',
        });
        this.isLoading = false;
      },
    });
  }
}
